import { SearchOutlined } from '@ant-design/icons';
import { Checkbox, Spin } from 'antd';
import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import { colorConstants } from 'config/colorConstants';
import {
    ppaSimFilterCategoryOptionTestId,
    ppaSimFilterCategoryPanelTestId,
    ppaSimFilterCategorySearchTestId,
    ppaSimFilterCategorySelectAllTestId,
} from 'constants/ppaTestIds';
import SearchEmptyResult from 'components/molecules/PricingSimulatorV3/SearchEmptyResult';

export type SimulatorFilterOption = {
    id: string;
    label: string;
};

type Props = {
    categoryKey: string;
    categoryLabel: string;
    options: SimulatorFilterOption[];
    isLoading?: boolean;
    selectedOptionIds: string[];
    onSelectedOptionIdsChange: (ids: string[]) => void;
    selectionDisabled?: boolean;
};

const StyledCategorySearchWrap = styled.div`
    width: 100%;
    height: 32px;
    flex-shrink: 0;
`;

const StyledCategorySearch = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 32px;
    box-sizing: border-box;
    padding: 5px 12px;
    border-radius: 4px;
    background: ${colorConstants.dropdownControlItemBg};
    cursor: text;

    .category-panel-search-icon {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        line-height: 0;
        color: ${colorConstants.greyAlt14};
        font-size: 16px;
    }

    .category-panel-search-input {
        flex: 1;
        min-width: 0;
        height: 22px;
        padding: 0;
        border: none;
        outline: none;
        background: transparent;
        font-size: 14px;
        line-height: 22px;
        color: ${colorConstants.blackTransparent88};
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;

        &::placeholder {
            color: ${colorConstants.simulatorSearchPlaceholder};
        }

        &:focus {
            outline: none;
        }
    }
`;

const StyledCategoryOptions = styled.div`
    width: 100%;
    max-height: 384px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    scrollbar-width: thin;
    scrollbar-color: ${colorConstants.borderGrey} ${colorConstants.greyAlt1};

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: ${colorConstants.greyAlt1};
    }

    &::-webkit-scrollbar-thumb {
        background: ${colorConstants.borderGrey};
        border-radius: 3px;
    }
`;

const StyledCategoryOptionRow = styled.div`
    display: flex;
    align-items: center;
    min-height: 32px;
    height: 32px;
    padding: 5px 12px;
    border-radius: 4px;
    box-sizing: border-box;

    &:hover {
        background: ${colorConstants.dropdownControlItemBg};
    }

    && .category-panel-option.ant-checkbox-wrapper {
        margin: 0;
        width: 100%;
        display: inline-flex;
        align-items: center;
        line-height: 22px;
        font-size: 14px;
        color: ${colorConstants.blackTransparent88};

        .ant-checkbox {
            top: 0;
            align-self: center;
        }

        .ant-checkbox::after {
            display: none;
        }

        .ant-checkbox-inner {
            width: 16px;
            height: 16px;
            border-radius: 4px;
            border-color: ${colorConstants.greyAlt};
        }

        .ant-checkbox + span {
            flex: 1;
            min-width: 0;
            padding: 0 0 0 8px;
            line-height: 22px;
            align-self: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &:hover .ant-checkbox-inner,
        .ant-checkbox:hover .ant-checkbox-inner,
        .ant-checkbox-input:focus + .ant-checkbox-inner {
            border-color: ${colorConstants.greyAlt};
            box-shadow: none;
        }

        .ant-checkbox-checked .ant-checkbox-inner,
        &:hover .ant-checkbox-checked .ant-checkbox-inner,
        .ant-checkbox-checked:hover .ant-checkbox-inner,
        .ant-checkbox-checked .ant-checkbox-input:focus + .ant-checkbox-inner {
            background-color: ${colorConstants.violet};
            border-color: ${colorConstants.violet};
            box-shadow: none;
        }
    }
`;

const StyledCategoryPanel = styled.div`
    display: flex;
    width: 245px;
    padding: 12px;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    border-radius: 8px;
    background: ${colorConstants.white};
    box-shadow: ${colorConstants.modalContentShadow};
`;

const SimulatorFilterCategoryPanel = ({
    categoryKey,
    categoryLabel,
    options,
    isLoading = false,
    selectedOptionIds,
    onSelectedOptionIdsChange,
    selectionDisabled = false,
}: Props) => {
    const [searchText, setSearchText] = useState('');

    const normalizedSearchText = searchText.trim().toLowerCase();
    const isSearchActive = normalizedSearchText.length > 0;

    const filteredOptions = useMemo(() => {
        if (!isSearchActive) {
            return options;
        }
        return options.filter((option) => option.label.toLowerCase().includes(normalizedSearchText));
    }, [isSearchActive, normalizedSearchText, options]);

    const selectedSet = useMemo(() => new Set(selectedOptionIds), [selectedOptionIds]);
    const hasNoSearchResults = isSearchActive && filteredOptions.length === 0;
    const allVisibleSelected =
        filteredOptions.length > 0 && filteredOptions.every((option) => selectedSet.has(option.id));

    const updateSelection = (nextIds: Set<string>) => {
        onSelectedOptionIdsChange(Array.from(nextIds));
    };

    const handleAllChange = (checked: boolean) => {
        if (selectionDisabled) return;
        const nextIds = new Set(selectedOptionIds);
        if (checked) {
            filteredOptions.forEach((option) => nextIds.add(option.id));
        } else {
            filteredOptions.forEach((option) => nextIds.delete(option.id));
        }
        updateSelection(nextIds);
    };

    const handleOptionChange = (optionId: string, checked: boolean) => {
        if (selectionDisabled) return;
        const nextIds = new Set(selectedOptionIds);
        if (checked) {
            nextIds.add(optionId);
        } else {
            nextIds.delete(optionId);
        }
        updateSelection(nextIds);
    };

    return (
        <StyledCategoryPanel data-testid={ppaSimFilterCategoryPanelTestId(categoryKey)}>
            <StyledCategorySearchWrap>
                <StyledCategorySearch>
                    <span className="category-panel-search-icon" aria-hidden>
                        <SearchOutlined />
                    </span>
                    <input
                        className="category-panel-search-input"
                        type="text"
                        role="searchbox"
                        placeholder={`Search ${categoryLabel}`}
                        value={searchText}
                        data-testid={ppaSimFilterCategorySearchTestId(categoryKey)}
                        onChange={(event) => setSearchText(event.target.value)}
                        aria-label={`Search ${categoryLabel}`}
                    />
                </StyledCategorySearch>
            </StyledCategorySearchWrap>

            <StyledCategoryOptions>
                {isLoading ? (
                    <div className="flex min-h-[120px] items-center justify-center">
                        <Spin size="small" />
                    </div>
                ) : hasNoSearchResults ? (
                    <SearchEmptyResult />
                ) : (
                    <>
                        <StyledCategoryOptionRow data-testid={ppaSimFilterCategorySelectAllTestId(categoryKey)}>
                            <Checkbox
                                className="category-panel-option"
                                checked={allVisibleSelected}
                                disabled={selectionDisabled}
                                onChange={(event) => handleAllChange(event.target.checked)}
                            >
                                All
                            </Checkbox>
                        </StyledCategoryOptionRow>

                        {filteredOptions.map((option) => (
                            <StyledCategoryOptionRow
                                key={option.id}
                                data-testid={ppaSimFilterCategoryOptionTestId(categoryKey, option.id)}
                            >
                                <Checkbox
                                    className="category-panel-option"
                                    checked={selectedSet.has(option.id)}
                                    disabled={selectionDisabled}
                                    onChange={(event) => handleOptionChange(option.id, event.target.checked)}
                                >
                                    {option.label}
                                </Checkbox>
                            </StyledCategoryOptionRow>
                        ))}
                    </>
                )}
            </StyledCategoryOptions>
        </StyledCategoryPanel>
    );
};

export default SimulatorFilterCategoryPanel;
